import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { useAnytimeTicketStore } from '../State/AnytimeTicketContext';
import { store as globalStore } from 'Models/Store';
import { usePaymentCheckOut } from 'Views/Components/_HumanWritten/Payment/PaymentCheckoutProvider';
import PaymentTypeWrapper from 'Views/Components/_HumanWritten/Payment/PaymentTypeWrapper';
import { useSetupPayment, SetupPaymentError } from 'Hooks/useSetupPayment';
import { PAYMENT_FORM_ID } from 'Views/Components/_HumanWritten/Payment/usePayment';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import { isNullOrUndefined } from 'Util/TypeGuards';
import { anytimeTicketKeys } from '../Hooks/anytimeTicketKeys';
import { MarketingSubscriptionCheckbox } from 'Views/Components/_HumanWritten/Marketing/MarketingSubscriptionCheckbox';
import { acknowledgeMarketingPreference } from 'Services/Api/AccountService';

/**
 * Component that holds the payment form.
 * Assumes parent has provided PaymentCheckOutProvider.
 */
export default function AnytimeTicketPaymentStep() {
	const transactionId = useAnytimeTicketStore(s => s.transactionId);
	const finishPurchase = useAnytimeTicketStore(s => s.finishPurchase);
	// The customer the ticket/POS item is being sold to (set by staff in the product step). Drives
	// which user's saved cards are offered for "pay with previous card". Falls back to the logged-in
	// user for self-purchases or when no customer is selected.
	const selectedCustomerId = useAnytimeTicketStore(s => s.selectedCustomerId);

	const {
		transactionDetails: fetchedTransactionDetails,
		error: setupError,
		isLoading: setupLoading,
	} = useSetupPayment({
		transactionId: transactionId ?? null,
		redirectUrl: '',
		isEventBooking: false,
		inCheckIn: false,
	});

	const queryClient = useQueryClient();
	const initializedRef = useRef(false);

	const { initializePaymentFlow, setTransactionDetails, setBeforePayment } = usePaymentCheckOut();

	// Marketing acknowledgment is collected only for non-staff self-purchases. State lives
	// here so it's tied to the lifetime of the (mounted) payment step — if the step never
	// renders (e.g. free purchase that skips to success) we never register beforePayment
	// and the user's preference is left untouched.
	const userId = globalStore.userId;
	const showMarketing = !globalStore.isStaff;
	const [marketingAck, setMarketingAck] = useState<boolean | undefined>(undefined);

	useEffect(() => {
		if (!showMarketing) return undefined;
		setBeforePayment(async () => {
			if (marketingAck !== undefined && userId) {
				try {
					await acknowledgeMarketingPreference(userId, marketingAck);
				} catch (err) {
					console.warn('acknowledgeMarketingPreference failed', err);
				}
			}
			return true;
		});
		return () => setBeforePayment(undefined);
	}, [showMarketing, marketingAck, userId, setBeforePayment]);

	useEffect(() => {
		if (transactionId && !initializedRef.current) {
			initializedRef.current = true;
			initializePaymentFlow({
				transactionId,
				userId: selectedCustomerId ?? globalStore.userId,
				paymentLocation: 'anytime-ticket',
				includeSubmitButtons: false,
				inCheckIn: false,
				onConfirm: async () => {
					queryClient.invalidateQueries({ queryKey: anytimeTicketKeys.all });
					queryClient.invalidateQueries({ queryKey: ['staffTransactions'] });
					finishPurchase();
				},
			});
		}
	}, [transactionId, selectedCustomerId, initializePaymentFlow, finishPurchase, queryClient]);

	useEffect(() => {
		if (fetchedTransactionDetails) {
			setTransactionDetails(fetchedTransactionDetails);
		}
	}, [fetchedTransactionDetails, setTransactionDetails]);

	// A $0 product (e.g. a free anytime ticket) needs no payment: the backend completes the
	// transaction during setup-payment and responds NO_PAYMENT_REQUIRED instead of returning
	// payment details. Treat that as a successful purchase — refresh the entitlement/transaction
	// lists and jump straight to the success step (mirroring the paid flow's onConfirm), rather
	// than falling through to the "Error loading payment details" fallback below.
	useEffect(() => {
		if (setupError === SetupPaymentError.NO_PAYMENT_REQUIRED) {
			queryClient.invalidateQueries({ queryKey: anytimeTicketKeys.all });
			queryClient.invalidateQueries({ queryKey: ['staffTransactions'] });
			finishPurchase();
		}
	}, [setupError, queryClient, finishPurchase]);

	if (isNullOrUndefined(transactionId)) {
		return <div className="p-8 text-center text-red-500">Error: Transaction lost. Please go back.</div>;
	}

	// Keep showing the spinner for a free purchase too — the effect above advances to the success
	// step on the next render, so this avoids briefly flashing the payment-error fallback.
	if (setupLoading || setupError === SetupPaymentError.NO_PAYMENT_REQUIRED) {
		return (
			<div className="flex flex-col items-center justify-center p-12">
				<LottieSpinner />
			</div>
		);
	}

	if (setupError !== SetupPaymentError.NONE || !fetchedTransactionDetails) {
		return <div className="p-8 text-center text-red-500">Error loading payment details. Please try again.</div>;
	}

	return (
		<div className="gift-voucher-payment">
			<PaymentTypeWrapper />
			{showMarketing && (
				<MarketingSubscriptionCheckbox userId={userId} value={marketingAck} onChange={setMarketingAck} />
			)}
		</div>
	);
}

interface FooterProps {
	onCancel: () => void;
	onDone: () => void;
	scrollModalToTop: () => void;
}

/**
 * Footer component that triggers the payment form submission.
 * Assumes parent has provided PaymentCheckOutProvider.
 */
AnytimeTicketPaymentStep.Footer = function AnytimeTicketPaymentFooter({ scrollModalToTop }: FooterProps) {
	const goToReview = useAnytimeTicketStore(s => s.goToReview);
	const { paymentProcessing } = usePaymentCheckOut();

	const onPayClick = () => {
		scrollModalToTop();
		const form = document.getElementById(PAYMENT_FORM_ID) as HTMLFormElement;
		if (form) {
			form.requestSubmit();
		}
	};

	return (
		<NewModal.Footer>
			<Button
				display={Display.Outline}
				colors={Colors.Grey}
				newStyles
				onClick={() => {
					goToReview();
					scrollModalToTop();
				}}
				disabled={paymentProcessing}
			>
				Back
			</Button>
			<Button
				display={Display.Solid}
				colors={Colors.Primary}
				newStyles
				disabled={paymentProcessing}
				onClick={onPayClick}
				buttonProps={{ 'data-test-id': 'anytime-ticket-pay-now' } as any}
			>
				{paymentProcessing ? 'Processing...' : 'Pay now'}
			</Button>
		</NewModal.Footer>
	);
};
